import React, { useEffect, useMemo, useState } from 'react';
import { type ConsentRequest, approveConsent, denyConsent, fetchConsentRequest } from '../utils/oauthApi';
import { useAuth } from '../contexts/AuthContext';

type Props = {
    requestId: string;
    token: string;
};

/**
 * Человекочитаемые названия прав. Скоуп, которого нет в словаре, показываем
 * как есть — лучше сырой идентификатор, чем молча спрятанное право.
 */
const SCOPE_LABELS: Record<string, { title: string; hint?: string }> = {
    openid: { title: 'Ваш идентификатор', hint: 'Постоянный номер аккаунта в SSMM' },
    profile: { title: 'Профиль', hint: 'Имя, фамилия и фотография' },
    email: { title: 'Электронная почта', hint: 'Адрес почты, привязанный к аккаунту' },
    phone: { title: 'Телефон', hint: 'Номер, привязанный к аккаунту' },
    offline_access: { title: 'Доступ без вашего участия', hint: 'Приложение сможет обновлять доступ, пока вы его не отзовёте' },
};

const PLATFORM_LABELS: Record<string, string> = {
    vk: 'ВКонтакте',
    telegram: 'Telegram',
    yandex: 'Яндекс',
    email: 'Почта',
};

/** expires_at приходит unix-таймстампом; сервер может отдать как секунды, так и миллисекунды. */
const toMillis = (expiresAt: number): number => (expiresAt > 1e12 ? expiresAt : expiresAt * 1000);

const formatLeft = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex flex-col justify-center items-center px-6 min-h-screen bg-zinc-900">{children}</div>
);

const OAuthConsent: React.FC<Props> = ({ requestId, token }) => {
    const { state } = useAuth();
    const [request, setRequest] = useState<ConsentRequest | null>(null);
    // Ошибка первичной загрузки запроса — показывать форму не из чего, поэтому
    // она подменяет собой весь экран (ниже, до основного return).
    const [loadError, setLoadError] = useState<string | null>(null);
    // Ошибка approve/deny — форма и кнопки должны остаться на месте, иначе
    // человек не поймёт, применилось ли решение, и не сможет повторить попытку.
    const [actionError, setActionError] = useState<string | null>(null);
    const [pending, setPending] = useState<'approve' | 'deny' | null>(null);
    // Тикает раз в секунду, пока запрос жив: по нему считаем остаток времени.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        fetchConsentRequest(requestId, token)
            .then(setRequest)
            .catch((e: Error) => setLoadError(e.message));
    }, [requestId, token]);

    useEffect(() => {
        if (!request) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [request]);

    const scopes = useMemo(
        () => (request?.scope ?? '').split(/\s+/).filter(Boolean),
        [request?.scope],
    );

    const msLeft = request ? toMillis(request.expires_at) - now : 0;
    const isExpired = Boolean(request) && msLeft <= 0;
    const busy = pending !== null;

    const decide = (decision: 'approve' | 'deny') => {
        setPending(decision);
        // Сбрасываем прошлую ошибку действия, чтобы старое сообщение не висело
        // поверх результата новой попытки.
        setActionError(null);
        const action = decision === 'approve' ? approveConsent : denyConsent;

        action(requestId, token)
            .then(({ redirect_to }) => {
                // Сервер иногда может ответить 200 без адреса — уводить браузер
                // на буквальную строку "undefined" нельзя, это тупик другого рода.
                if (!redirect_to) {
                    setActionError('Сервер не вернул адрес перехода. Попробуйте ещё раз.');
                    setPending(null);
                    return;
                }
                window.location.href = redirect_to;
            })
            .catch((e: Error) => {
                setActionError(e.message);
                setPending(null);
            });
    };

    if (loadError) {
        return (
            <Shell>
                <div className="w-full max-w-md text-center">
                    <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 rounded-xl border bg-red-500/10 border-red-500/30">
                        <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        </svg>
                    </div>
                    <h1 className="text-lg font-semibold text-white">Запрос недоступен</h1>
                    <p className="mt-2 text-sm break-words text-zinc-500">{loadError}</p>
                    <p className="mt-4 text-xs text-zinc-600">Вернитесь в приложение и начните вход заново.</p>
                </div>
            </Shell>
        );
    }

    if (!request) {
        return (
            <Shell>
                <div className="w-8 h-8 rounded-full border-2 animate-spin border-zinc-700 border-t-white" />
                <p className="mt-4 text-sm text-zinc-500">Загружаем запрос…</p>
            </Shell>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-zinc-900">
            {/* Header */}
            <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
                <div className="flex justify-center items-center mb-3">
                    <div className="flex justify-center items-center w-10 h-10 rounded-xl bg-zinc-800">
                        <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                </div>
                <h1 className="text-xl font-semibold text-center text-white">Разрешить доступ?</h1>
                <p className="mt-1 text-sm text-center break-words text-zinc-500">
                    <span className="font-medium text-zinc-300">{request.client_name}</span> запрашивает доступ к вашему аккаунту
                </p>
            </div>

            {/* Content */}
            <div className="flex-1 px-6 py-6 mx-auto w-full max-w-md">
                {/* Кому даём доступ: имя клиента — просто текст, выбранный при
                    регистрации, а хост возврата привязан к тому, куда реально
                    уйдёт код. Поэтому хост показан отдельной, заметной строкой. */}
                <div className="p-4 mb-3 rounded-xl border bg-zinc-800/40 border-zinc-800">
                    <div className="flex items-center space-x-3">
                        <div className="flex flex-shrink-0 justify-center items-center w-11 h-11 rounded-xl bg-zinc-700/50">
                            <span className="text-base font-semibold text-zinc-300">
                                {(request.client_name || '?').charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white break-words">{request.client_name}</div>
                            <div className="text-xs text-zinc-500">Внешнее приложение</div>
                        </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-zinc-800">
                        <div className="text-xs text-zinc-500">После разрешения вы перейдёте на</div>
                        <div className="flex items-center mt-1 space-x-1.5">
                            <svg className="flex-shrink-0 w-3.5 h-3.5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 0 0-5.656 0l-2.829 2.828a4 4 0 1 0 5.657 5.657l1.414-1.414m-1.243-6.415a4 4 0 0 1 5.656 0l.001.001a4 4 0 0 1 0 5.656l-2.829 2.828" />
                            </svg>
                            <span className="font-mono text-sm break-all text-zinc-200">{request.client_redirect_host}</span>
                        </div>
                    </div>
                </div>

                {/* Чей аккаунт отдаём — чтобы человек не разрешил доступ из-под
                    чужой сохранённой сессии, не заметив этого. */}
                {state.user && (
                    <div className="flex items-center p-3 mb-3 space-x-3 rounded-xl border bg-zinc-800/20 border-zinc-800/70">
                        <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-800">
                            {state.user.avatar ? (
                                <img src={state.user.avatar} alt="" className="object-cover w-full h-full" />
                            ) : (
                                <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                                    {state.user.name.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">{state.user.name}</div>
                            <div className="text-xs text-zinc-500">
                                {PLATFORM_LABELS[state.user.platform] ?? state.user.platform}
                            </div>
                        </div>
                    </div>
                )}

                {/* Права */}
                {scopes.length > 0 && (
                    <div className="p-4 mb-3 rounded-xl border bg-zinc-800/40 border-zinc-800">
                        <div className="mb-3 text-xs font-medium tracking-wide uppercase text-zinc-500">
                            Приложение получит
                        </div>
                        <ul className="space-y-2.5">
                            {scopes.map((scope) => {
                                const label = SCOPE_LABELS[scope];
                                return (
                                    <li key={scope} className="flex items-start">
                                        <svg className="flex-shrink-0 mt-0.5 mr-2.5 w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <div className="min-w-0">
                                            <div className="text-sm break-words text-zinc-200">
                                                {label ? label.title : <span className="font-mono">{scope}</span>}
                                            </div>
                                            {label?.hint && (
                                                <div className="mt-0.5 text-xs break-words text-zinc-500">{label.hint}</div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* Технические детали запроса — мелким шрифтом, для тех, кто сверяет. */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 mb-5 text-xs text-zinc-600">
                    {request.resource && (
                        <span className="break-all">
                            Ресурс: <span className="font-mono text-zinc-500">{request.resource}</span>
                        </span>
                    )}
                    <span className={isExpired ? 'text-orange-400' : ''}>
                        {isExpired ? 'Запрос истёк' : `Действителен ещё ${formatLeft(msLeft)}`}
                    </span>
                </div>

                {isExpired && (
                    <div className="flex items-start p-3 mb-4 rounded-xl border bg-orange-500/10 border-orange-500/30">
                        <svg className="flex-shrink-0 mt-0.5 mr-2.5 w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l2.5 2.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                        </svg>
                        <p className="text-xs leading-relaxed text-orange-300/90">
                            Время на подтверждение вышло. Вернитесь в приложение и начните вход заново.
                        </p>
                    </div>
                )}

                {actionError && (
                    <div className="flex items-start p-3 mb-4 rounded-xl border bg-red-500/10 border-red-500/30">
                        <svg className="flex-shrink-0 mt-0.5 mr-2.5 w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm break-words text-red-400">{actionError}</p>
                    </div>
                )}

                {/* Действия */}
                <div className="space-y-3">
                    <button
                        type="button"
                        disabled={busy || isExpired}
                        onClick={() => decide('approve')}
                        className="flex justify-center items-center px-4 py-3.5 w-full font-medium rounded-xl border transition-colors bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {pending === 'approve' ? (
                            <>
                                <span className="mr-2 w-4 h-4 rounded-full border-2 animate-spin border-emerald-400 border-t-transparent" />
                                <span>Разрешаем…</span>
                            </>
                        ) : (
                            <>
                                <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Разрешить доступ</span>
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide('deny')}
                        className="flex justify-center items-center px-4 py-3 w-full font-medium rounded-xl border transition-colors bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {pending === 'deny' ? (
                            <>
                                <span className="mr-2 w-4 h-4 rounded-full border-2 animate-spin border-zinc-400 border-t-transparent" />
                                <span>Отклоняем…</span>
                            </>
                        ) : (
                            <span>Отклонить</span>
                        )}
                    </button>
                </div>

                <p className="mt-5 text-xs leading-relaxed text-center text-zinc-600">
                    Разрешайте доступ, только если узнаёте приложение и адрес{' '}
                    <span className="font-mono break-all text-zinc-500">{request.client_redirect_host}</span>.
                </p>
            </div>
        </div>
    );
};

export default OAuthConsent;
